// src/pages/clientes/components/EmpresasTab.tsx
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui';
import { EmptyState } from '@/components/common/EmptyState';
import { Skeleton } from '@/components/common/Skeleton';
import { Plus, Building2, Trash2 } from 'lucide-react';
import { useEmpresas, useCrearEmpresa, useEliminarEmpresa } from '@/hooks/queries/useClientes';
import { empresaSchema, type EmpresaFormData } from '../schemas/clienteSchema';
import type { Empresa } from '@/types';

interface Props {
  clienteId: string;
}

export function EmpresasTab({ clienteId }: Props) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [empresaToDelete, setEmpresaToDelete] = useState<Empresa | null>(null);

  const { data: empresas, isLoading } = useEmpresas(clienteId);
  const crearMutation = useCrearEmpresa(clienteId);
  const eliminarMutation = useEliminarEmpresa(clienteId);

  const form = useForm<EmpresaFormData>({
    resolver: zodResolver(empresaSchema),
    defaultValues: { nombre: '', codigo: '' },
  });

  const onSubmit = async (data: EmpresaFormData) => {
    try {
      await crearMutation.mutateAsync(data);
      form.reset();
      setIsFormOpen(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    if (!empresaToDelete) return;
    try {
      await eliminarMutation.mutateAsync(empresaToDelete.id);
      setEmpresaToDelete(null);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <Card className="mt-4">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <Skeleton className="h-4 w-48" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mt-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Empresas del cliente</h3>
          <Button size="sm" onClick={() => setIsFormOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Empresa
          </Button>
        </div>
        <CardContent className="pt-4">
          {empresas && empresas.length > 0 ? (
            <div className="space-y-3">
              {empresas.map((empresa: Empresa) => (
                <div
                  key={empresa.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                      <Building2 className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">{empresa.nombre}</p>
                      {empresa.codigo && (
                        <p className="text-sm text-gray-500">Código: {empresa.codigo}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={empresa.activo ? 'success' : 'default'}>
                      {empresa.activo ? 'Activa' : 'Inactiva'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEmpresaToDelete(empresa)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Building2 className="h-12 w-12" />}
              title="Sin empresas"
              description="Este cliente no tiene empresas registradas"
              action={{
                label: 'Agregar empresa',
                onClick: () => setIsFormOpen(true),
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Dialog de nueva empresa */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Nueva Empresa</DialogTitle>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Nombre *"
              placeholder="Nombre de la empresa"
              {...form.register('nombre')}
              error={form.formState.errors.nombre?.message}
            />
            <Input
              label="Código"
              placeholder="Código interno"
              {...form.register('codigo')}
              error={form.formState.errors.codigo?.message}
            />
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setIsFormOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" loading={crearMutation.isPending}>
                Crear
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={!!empresaToDelete} onOpenChange={() => setEmpresaToDelete(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Eliminar Empresa</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            ¿Estás seguro de que deseas eliminar la empresa{' '}
            <span className="font-semibold">{empresaToDelete?.nombre}</span>?
          </p>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEmpresaToDelete(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={eliminarMutation.isPending}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
